import { Action } from '@ngrx/store';

import { AiStreamFrame } from '@domain/ai-assistant/ai-assistant-stream.models';

import { streamDone, streamErrored, streamFramesReceived, streamWaitingForConsent } from '../ai-assistant.actions';

const isTerminalFrame = (frame: AiStreamFrame): boolean =>
  frame.type === 'DONE' || frame.type === 'RUN_WAITING_FOR_TOOL_CONSENT' || frame.type === 'ERROR';

/**
 * Converts a buffered batch of SSE frames into an ordered array of NgRx actions.
 *
 * Content frames (OUTPUT, REASONING, TOOL_CALL) are emitted first via
 * `streamFramesReceived` so the tool-call part is accumulated into state before
 * the terminal action fires. Terminal priority: consent > error > done.
 */
export const batchFramesToActions = (conversationId: string, frames: AiStreamFrame[]): Action[] => {
  const contentFrames = frames.filter((f) => !isTerminalFrame(f));
  const actions: Action[] = [];

  if (contentFrames.length > 0) {
    actions.push(streamFramesReceived({ conversationId, frames: contentFrames }));
  }

  // Priority: RUN_WAITING_FOR_TOOL_CONSENT > ERROR > DONE so that consent/error
  // always win over the preceding DONE frame that arrives in the same buffer tick.
  const consentFrame = frames.find((f) => f.type === 'RUN_WAITING_FOR_TOOL_CONSENT');
  const errorFrame = frames.find((f) => f.type === 'ERROR');
  const doneFrame = frames.find((f) => f.type === 'DONE');

  if (consentFrame?.pendingToolConsent) {
    actions.push(streamWaitingForConsent({ conversationId, pendingToolConsent: consentFrame.pendingToolConsent }));
  } else if (errorFrame) {
    actions.push(streamErrored({ conversationId, message: errorFrame.message ?? 'AI assistant error' }));
  } else if (doneFrame) {
    actions.push(streamDone({ conversationId, finishReason: doneFrame.finishReason }));
  }

  return actions;
};
